import { inject, Injectable } from '@angular/core';
import { Constants } from '@core/app-constants';
import {
  AddToBasketPartialSuccessResponseDto,
  AddToBasketSeatRequestDto,
  AddToBasketSuccessResponseDto,
  ApiException,
  ApiResponse,
  CreateRebookingBeneficiaryRequestDto,
  CustomerGroupClient,
  CustomerGroupResponseDto,
  GetCustomerGroupsResponse,

  CustomerSubscriptionsResponseDto,
  OrderCustomerTypeEnumResponseDto,
  RebookingBeneficiaryResponseDto,
  RebookingNewSeatResponseDto,
  RebookingRenewalStatusDto,
  RebookingSeatResponseDto,
  RebookingSeatsAvailabilityResponseDto,
  RebookingSeatSelectionRequestDto,
  RebookingSeatSelectionSuccessResponseDto,
  RebookingSeatsResponseDto,
  RebookingSelectedSeatRequestDto,
  RebookingSelectedSeatResponseDto,
  ResubscriptionBeneficiaryAndSeatDetailsResponseDto,
  ResubscriptionCategoryPriceResponseDto,
  ResubscriptionResponseDto,
  SeatRebookingClient,
} from '@core/services/apiclient';
import { HttpHandlerService } from '@core/services/http-handler.service';
import { select, Store } from '@ngrx/store';
import * as fromSales from '@root-store/sales-store';
import { CustomerService } from '@shared/customer/services/customer.service';
import { map, Observable, of, switchMap, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SalesResubscriptionService {
  private readonly customerGroupClient = inject(CustomerGroupClient);
  private readonly seatRebookingClient = inject(SeatRebookingClient);
  private readonly customerService = inject(CustomerService);
  private readonly httpHandlerService = inject(HttpHandlerService);
  private readonly store = inject(Store<fromSales.State>);

  public getCustomerCurrentSubscriptions(customerId: number): Observable<CustomerSubscriptionsResponseDto[]> {
    return this.seatRebookingClient
      .getCustomerCurrentSubscriptions(customerId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<CustomerSubscriptionsResponseDto[]>());
  }

  public getCustomerGroup(): Observable<CustomerGroupResponseDto[]> {
    return this.customerGroupClient
      .get(Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<GetCustomerGroupsResponse>(),
        map((response) => response.groups ?? []),
      );
  }

  public getRebookingAccessStatus(
    customerId: number,
    resubscriptionIds: number[],
  ): Observable<RebookingRenewalStatusDto[]> {
    return this.seatRebookingClient
      .getRebookingAccessStatus(customerId, resubscriptionIds, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<RebookingRenewalStatusDto[]>());
  }

  public getAllResubscriptions(): Observable<ResubscriptionResponseDto[]> {
    return this.seatRebookingClient
      .getAll(Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<ResubscriptionResponseDto[]>());
  }

  public getResusbcriptionPrices(
    subscriptionId: number,
    channelId: number,
    categoryIds: number[],
  ): Observable<ResubscriptionCategoryPriceResponseDto[]> {
    return this.seatRebookingClient
      .getSubscriptionPrices(subscriptionId, channelId, categoryIds, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<ResubscriptionCategoryPriceResponseDto[]>());
  }

  public getResubscriptionBeneficairesAndSeats(
    basketId: number,
    subscriptionId: number,
    customerId: number,
  ): Observable<ResubscriptionBeneficiaryAndSeatDetailsResponseDto[]> {
    return this.seatRebookingClient
      .getSubscriptionDetails(basketId, subscriptionId, customerId, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<RebookingSeatsResponseDto>(),
        map((response: RebookingSeatsResponseDto) => response.seats),
      );
  }

  public getSubscriptionAvailableSeatRate(
    basketId: number,
    subscriptionId: number,
    customerId: number,
    seatId: number,
  ): Observable<ApiResponse<RebookingSeatResponseDto>> {
    return this.seatRebookingClient.getSubscriptionAvailableSeatRate(
      basketId,
      subscriptionId,
      customerId,
      seatId,
      Constants.ApiVersion,
    );
  }

  public evaluateSeatsAvailabilityForSubscription(
    subscriptionId: number,
    seatIds: number[],
  ): Observable<RebookingSeatsAvailabilityResponseDto[]> {
    return this.seatRebookingClient
      .evaluateSeatsAvailabilityForSubscription(subscriptionId, seatIds, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<RebookingSeatsAvailabilityResponseDto[]>());
  }

  public getSubscriptionSelectedSeatAvailability(
    subscriptionId: number,
    seatId: number,
  ): Observable<ApiResponse<RebookingNewSeatResponseDto>> {
    return this.seatRebookingClient.getSubscriptionSelectedSeatAvailability(
      subscriptionId,
      seatId,
      Constants.ApiVersion,
    );
  }

  public addToBasket(
    addToBasketSeatsRequestDtos: AddToBasketSeatRequestDto[],
  ): Observable<AddToBasketSuccessResponseDto> {
    return this.store.pipe(
      take(1),
      select(fromSales.selectors.selectBasketId),
      switchMap((basketId) =>
        basketId && basketId > 0
          ? this.seatRebookingClient
              .addToBasket(basketId, addToBasketSeatsRequestDtos, Constants.ApiVersion)
              .pipe(this.httpHandlerService.handleApiResponse<AddToBasketSuccessResponseDto>())
          : of(null),
      ),
    );
  }

  public getAddToBasketErrorMessage(error: ApiException): {
    message: string;
    type: 'danger' | 'partialSuccess';
    partialSuccessValue?: AddToBasketPartialSuccessResponseDto;
  } {
    switch (error.status) {
      case 207:
        return {
          message: 'sales.resubscription.subscriptionsWithUnavailableSeatsHaveNotBeenAddedToBasket',
          type: 'partialSuccess',
          partialSuccessValue: error.result,
        };
      case 422:
        return {
          message: 'sales.resubscription.seatsForTheseSubscriptionsAreUnavailable',
          type: 'danger',
        };
      default:
        return {
          message: 'sales.resubscription.anErrorOccuredWhileAddingSeatsToBasket',
          type: 'danger',
        };
    }
  }

  public selectResubSeatAsync(
    BasketId: number,
    subscriptionId: number,
    data: RebookingSeatSelectionRequestDto,
  ): Observable<RebookingSeatSelectionSuccessResponseDto> {
    return this.seatRebookingClient
      .selectSeat(BasketId, subscriptionId, data, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<RebookingSeatSelectionSuccessResponseDto>());
  }

  public getRebookingSelectedSeats(
    seats: RebookingSelectedSeatRequestDto[],
  ): Observable<RebookingSelectedSeatResponseDto[]> {
    return this.seatRebookingClient
      .getRebookingSelectedSeats(seats, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<RebookingSelectedSeatResponseDto[]>());
  }

  public holdRebookingSeats(
    basketId: number,
    subscriptionId: number,
    seatIds: number[],
  ): Observable<ApiResponse<void>> {
    return this.seatRebookingClient.holdRebookingSeats(
      basketId,
      subscriptionId,
      seatIds,
      Constants.ApiVersion,
    );
  }

  public getBeneficiariesInfo(beneficiaryIds: number[]): Observable<RebookingBeneficiaryResponseDto[]> {
    return this.seatRebookingClient
      .getBeneficiaries(beneficiaryIds, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<RebookingBeneficiaryResponseDto[]>());
  }

  public searchBeneficiaries(searchTerms: string): Observable<RebookingBeneficiaryResponseDto[]> {
    return this.customerService.searchBeneficiaries(searchTerms).pipe(
      map((items) =>
        (items ?? []).map(
          (item) =>
            ({
              id: item.id,
              name: item.name,
              email: item.email,
              zipCode: item.zipCode,
              type:
                item.customerType === 'Moral'
                  ? OrderCustomerTypeEnumResponseDto.Moral
                  : OrderCustomerTypeEnumResponseDto.Physical,
            } as RebookingBeneficiaryResponseDto),
        ),
      ),
    );
  }

  public createResubscriptionBeneficiary(
    request: CreateRebookingBeneficiaryRequestDto,
  ): Observable<RebookingBeneficiaryResponseDto> {
    return this.seatRebookingClient
      .createBeneficiary(request, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<RebookingBeneficiaryResponseDto>());
  }
}
